import { useState, useCallback } from "react";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../../../../services/firebase";
import { useTenant } from "../../../../hooks/useTenant";
import { COLLECTIONS } from "../../../../services/collections";
import { enrollmentFromFirestore, enrollmentToFirestore } from "../../../../models/enrollment/enrollmentConverter";
import { toISODate } from "../../../../utils/date";

/**
 * useTrialsData
 *
 * Busca aulas experimentais somente quando `fetchTrials` é chamado explicitamente.
 * Não carrega automaticamente — aguarda o clique no botão "Buscar".
 */
export const useTrialsData = () => {
    const { idTenant } = useTenant();
    const [trials, setTrials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);

    const fetchTrials = useCallback(async (periodStart, periodEnd) => {
        if (!idTenant || !periodStart || !periodEnd) return;

        setLoading(true);
        setError(null);

        try {
            const startStr = typeof periodStart === "string" ? periodStart : toISODate(periodStart);
            const endStr = typeof periodEnd === "string" ? periodEnd : toISODate(periodEnd);

            const converter = { toFirestore: enrollmentToFirestore, fromFirestore: enrollmentFromFirestore };
            const trialsRef = collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS).withConverter(converter);
            const q = query(
                trialsRef,
                where("type", "==", "trial"),
                where("sessionDate", ">=", startStr),
                where("sessionDate", "<=", endStr),
                orderBy("sessionDate", "desc")
            );

            const snapshot = await getDocs(q);
            const data = snapshot.docs.map((doc) => doc.data()).filter(d => d.status !== 'canceled');

            // Ordenação: data DESC, hora DESC.
            data.sort((a, b) => {
                const dateA = a.sessionDate || "";
                const dateB = b.sessionDate || "";
                if (dateA !== dateB) return dateB.localeCompare(dateA);

                const timeA = a.sessionTime || "";
                const timeB = b.sessionTime || "";
                return timeB.localeCompare(timeA);
            });

            setTrials(data);
            setHasFetched(true);
        } catch (err) {
            console.error("Erro ao carregar experimentais:", err);
            if (err.message && err.message.includes("requires an index")) {
                setError("O sistema está configurando a base de dados. Recarregue a página em alguns instantes.");
            } else {
                setError("Falha ao carregar aulas experimentais.");
            }
        } finally {
            setLoading(false);
        }
    }, [idTenant]);

    return { trials, loading, error, hasFetched, fetchTrials };
};
